/* eslint-disable no-unused-vars */
import CardInformation from "@components/Cards/CardInformation";
import { calculatePensionsCost } from "@utils/calculator";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ReactComponent as InfoIcon } from "../../assets/icons/info.svg";
import { ReactComponent as MinusIcon } from "../../assets/icons/minus.svg";
import { ReactComponent as PlusIcon } from "../../assets/icons/plus.svg";
import BottomBar from "../../components/BottomBars/BottomBar";
import PennyInput from "../../components/Inputs/PennyInput";
import { ObjectDataProps } from "./PensionStep";
import HeaderProgress from "@components/Progress/HeaderProgress";

type PensionStepOneProps = {
  onNextCallback(objectData: ObjectDataProps): void;
};

const PensionStepOne: React.FC<PensionStepOneProps> = ({ onNextCallback }) => {
  const navigate = useNavigate();

  const [todayWantToSaveField, setTodayWantToSaveField] = useState(243);
  const [enjoyRetirementField, setEnjoyRetirementField] = useState(3500);
  const [currentAgeField, setCurrentAgeField] = useState(25);
  const [retirementAgeField, setRetirementAgeField] = useState(65);
  const [currentPensionFundField, setCurrentPensionFundField] =
    useState(348000);
  const [calculationResults, setCalculationResults] = useState<any>(null); // State to store calculation results

  // Today want to save
  const [clickCount, setClickCount] = useState(0);
  const todayWantToSaveCount = useRef<any>(null);
  const resetClickCountTimeout = useRef<any>(null);

  const clearAutoIncrementDecrement = () => {
    if (todayWantToSaveCount.current) {
      clearInterval(todayWantToSaveCount.current);
      todayWantToSaveCount.current = null;
    }
  };

  const resetClickCount = () => {
    if (resetClickCountTimeout.current) {
      clearTimeout(resetClickCountTimeout.current);
    }
    resetClickCountTimeout.current = setTimeout(() => {
      setClickCount(0);
    }, 1000); // Reset click count after 1 second of inactivity
  };
  const onEventTodayWantToSaveField = (
    action:
      | "start-count-increment"
      | "start-count-decrement"
      | "stop-count"
      | "increment"
      | "decrement"
  ) => {
    switch (action) {
      case "start-count-increment":
      case "start-count-decrement":
        clearAutoIncrementDecrement();
        todayWantToSaveCount.current = setInterval(() => {
          setTodayWantToSaveField((prev) => {
            const step = clickCount >= 5 ? 50 : 100;
            const nextValue =
              action === "start-count-increment"
                ? Math.min(prev + step, 5000)
                : Math.max(prev - step, 43);
            return nextValue;
          });
          setClickCount((prev) => prev + 1);
        }, 1000);
        break;

      case "stop-count":
        clearAutoIncrementDecrement();
        resetClickCount();
        break;

      case "increment":
      case "decrement":
        const step = clickCount >= 5 ? 50 : 1;
        setTodayWantToSaveField((prev) => {
          const nextValue =
            action === "increment"
              ? Math.min(prev + step, 5000)
              : Math.max(prev - step, 43);
          return nextValue;
        });
        resetClickCount();
        setClickCount((prev) => prev + 1);
        break;

      default:
        break;
    }
  };

  // Current age
  const currentAgeFieldIntervalCount = useRef<any>(null);
  const onEventCurrentAgeField = (
    action:
      | "start-count-increment"
      | "start-count-decrement"
      | "stop-count"
      | "increment"
      | "decrement"
  ) => {
    switch (action) {
      case "start-count-increment": {
        currentAgeFieldIntervalCount.current = setInterval(() => {
          setCurrentAgeField((prev) => {
            if (prev < 55) {
              return prev + 1;
            } else {
              return prev;
            }
          });
        }, 150);
        break;
      }
      case "start-count-decrement": {
        currentAgeFieldIntervalCount.current = setInterval(() => {
          setCurrentAgeField((prev) => {
            if (prev > 20) {
              return prev - 1;
            } else {
              return prev;
            }
          });
        }, 150);
        break;
      }
      case "stop-count": {
        clearInterval(currentAgeFieldIntervalCount.current);
        break;
      }
      case "increment": {
        setCurrentAgeField((prev) => {
          if (prev < 55) {
            return prev + 1;
          } else {
            return prev;
          }
        });
        break;
      }
      case "decrement": {
        setCurrentAgeField((prev) => {
          if (prev > 20) {
            return prev - 1;
          } else {
            return prev;
          }
        });
        break;
      }
      default: {
        break;
      }
    }
  };

  // To enjoy retirement
  const retirementEnjoyFieldIntervalCounter = useRef<any>(null);
  const onEventRetirementEnjoyField = (
    action:
      | "start-count-increment"
      | "start-count-decrement"
      | "stop-count"
      | "increment"
      | "decrement"
  ) => {
    switch (action) {
      case "start-count-increment": {
        retirementEnjoyFieldIntervalCounter.current = setInterval(() => {
          setEnjoyRetirementField((prev) => {
            if (prev < 4901) {
              return prev + 100;
            } else {
              return prev;
            }
          });
        }, 150);
        break;
      }
      case "start-count-decrement": {
        retirementEnjoyFieldIntervalCounter.current = setInterval(() => {
          setEnjoyRetirementField((prev) => {
            if (prev > 99) {
              return prev - 100;
            } else {
              return prev;
            }
          });
        }, 150);
        break;
      }
      case "stop-count": {
        clearInterval(retirementEnjoyFieldIntervalCounter.current);
        break;
      }
      case "increment": {
        setEnjoyRetirementField((prev) => {
          if (prev < 4901) {
            return prev + 100;
          } else {
            return prev;
          }
        });
        break;
      }
      case "decrement": {
        setEnjoyRetirementField((prev) => {
          if (prev > 99) {
            return prev - 100;
          } else {
            return prev;
          }
        });
        break;
      }
      default: {
        break;
      }
    }
  };

  // Age retirement
  const retirementAgeFieldIntervalCounter = useRef<any>(null);
  const onEventRetirementAgeField = (
    action:
      | "start-count-increment"
      | "start-count-decrement"
      | "stop-count"
      | "increment"
      | "decrement"
  ) => {
    switch (action) {
      case "start-count-increment": {
        retirementAgeFieldIntervalCounter.current = setInterval(() => {
          setRetirementAgeField((prev) => {
            if (prev < 75) {
              return prev + 1;
            } else {
              return prev;
            }
          });
        }, 100);
        break;
      }
      case "start-count-decrement": {
        retirementAgeFieldIntervalCounter.current = setInterval(() => {
          setRetirementAgeField((prev) => {
            if (prev > 60) {
              return prev - 1;
            } else {
              return prev;
            }
          });
        }, 100);
        break;
      }
      case "stop-count": {
        clearInterval(retirementAgeFieldIntervalCounter.current);
        break;
      }
      case "increment": {
        if (retirementAgeField < 75) {
          setRetirementAgeField((prev) => prev + 1);
        }
        break;
      }
      case "decrement": {
        if (retirementAgeField > 60) {
          setRetirementAgeField((prev) => prev - 1);
        }
        break;
      }
      default: {
        break;
      }
    }
  };

  const onInputChecker = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (/^(?:\d+(?:\.\d*)?|\.\d+)?$/.test(e.target.value)) {
      return setCurrentPensionFundField(Number(e.target.value));
    }
  };

  const onSubmitNext = useCallback(() => {
    let submit_object = {
      todaySave: todayWantToSaveField,
      enjoy_retirement: enjoyRetirementField,
      current_age: currentAgeField,
      retirement_age: retirementAgeField,
      current_pension_fund: currentPensionFundField,
    };

    onNextCallback(submit_object as any);
  }, [
    enjoyRetirementField,
    todayWantToSaveField,
    currentAgeField,
    retirementAgeField,
    currentPensionFundField,
    onNextCallback,
  ]);

  useEffect(() => {
    const results = calculatePensionsCost(
      currentAgeField,
      retirementAgeField,
      enjoyRetirementField,
      1,
      currentPensionFundField
    );
    setCalculationResults(results);
  }, [
    enjoyRetirementField,
    currentAgeField,
    retirementAgeField,
    currentPensionFundField,
  ]);

  return (
    <>
      <div className="flex flex-col pb-2 md:pb-0">
        <div className="py-4 px-4 md:py-6 md:px-8 w-full flex flex-col gap-y-3">
          <span className="text-pn-caption-medium hidden md:block">
            Pension Calculator
          </span>
          <div className="flex flex-col gap-y-1">
            <span className="text-pn-large-title text-pn_gray-950">
              Calculate your pension
            </span>
            <span className="text-pn-body-regular text-pn_gray-600">
              Tell us the goals you have for your pension below
            </span>
          </div>
        </div>

        <div className="px-4 py-4 md:px-8 md:py-0 w-full flex flex-col gap-y-6 md:gap-y-2">
          <div className="flex flex-col md:flex-row gap-6">
            <div className="flex flex-1 flex-col gap-y-2">
              <span className="text-pn-callout-emphasis text-pn_gray-950 bg-[#F4E34E] w-fit">
                Today, I want to save:
              </span>
              <div className="flex items-center gap-x-2">
                <div className="flex items-center gap-x-1 flex-1 md:flex-[none] md:gap-x-3">
                  <span className="text-pn-large-title text-pn_gray-950">
                    € {todayWantToSaveField}
                  </span>
                  <sub className="text-pn-caption-regular pt-3 text-pn_gray-600">
                    per month
                  </sub>
                </div>
                <div className="flex items-center gap-x-4">
                  <button
                    onMouseDown={() =>
                      onEventTodayWantToSaveField("start-count-decrement")
                    }
                    onMouseUp={() => onEventTodayWantToSaveField("stop-count")}
                    onMouseLeave={() =>
                      onEventTodayWantToSaveField("stop-count")
                    }
                    onClick={() => onEventTodayWantToSaveField("decrement")}
                    className="btn-tertiary-icon-sm"
                  >
                    <MinusIcon />
                  </button>
                  <button
                    onMouseDown={() =>
                      onEventTodayWantToSaveField("start-count-increment")
                    }
                    onMouseUp={() => onEventTodayWantToSaveField("stop-count")}
                    onMouseLeave={() =>
                      onEventTodayWantToSaveField("stop-count")
                    }
                    onClick={() => onEventTodayWantToSaveField("increment")}
                    className="btn-tertiary-icon-sm"
                  >
                    <PlusIcon />
                  </button>
                </div>
              </div>
            </div>
            <div className="flex flex-1 flex-col gap-y-2">
              <span className="text-pn-callout-emphasis text-pn_gray-950 bg-[#F4E34E] w-fit">
                To enjoy in my retirement:
              </span>
              <div className="flex items-center gap-x-2">
                <div className="flex items-center gap-x-1 flex-1 md:flex-[none] md:gap-x-3">
                  <span className="text-pn-large-title text-pn_gray-950">
                    € {enjoyRetirementField}
                  </span>
                  <sub className="text-pn-caption-regular pt-3 text-pn_gray-600">
                    per month
                  </sub>
                </div>
                <div className="flex items-center gap-x-4">
                  <button
                    onMouseDown={() =>
                      onEventRetirementEnjoyField("start-count-decrement")
                    }
                    onMouseUp={() => onEventRetirementEnjoyField("stop-count")}
                    onMouseLeave={() =>
                      onEventRetirementEnjoyField("stop-count")
                    }
                    onClick={() => onEventRetirementEnjoyField("decrement")}
                    className="btn-tertiary-icon-sm"
                  >
                    <MinusIcon />
                  </button>
                  <button
                    onMouseDown={() =>
                      onEventRetirementEnjoyField("start-count-increment")
                    }
                    onMouseUp={() => onEventRetirementEnjoyField("stop-count")}
                    onMouseLeave={() =>
                      onEventRetirementEnjoyField("stop-count")
                    }
                    onClick={() => onEventRetirementEnjoyField("increment")}
                    className="btn-tertiary-icon-sm"
                  >
                    <PlusIcon />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="divider-dotted-gray my-2 hidden md:block"></div>

          <div className="flex flex-col md:flex-row gap-6">
            <div className="flex flex-1 flex-col gap-y-2">
              <span className="text-pn-callout-emphasis text-pn_gray-950 bg-[#F4E34E] w-fit">
                I am now:
              </span>
              <div className="flex items-center gap-x-2">
                <div className="flex items-center gap-x-1 flex-1 md:flex-[none] md:gap-x-3">
                  <span className="text-pn-large-title text-pn_gray-950">
                    {currentAgeField}
                  </span>
                  <sub className="text-pn-caption-regular pt-3 text-pn_gray-600">
                    years old
                  </sub>
                </div>
                <div className="flex items-center gap-x-4">
                  <button
                    onMouseDown={() =>
                      onEventCurrentAgeField("start-count-decrement")
                    }
                    onMouseUp={() => onEventCurrentAgeField("stop-count")}
                    onMouseLeave={() => onEventCurrentAgeField("stop-count")}
                    onClick={() => onEventCurrentAgeField("decrement")}
                    className="btn-tertiary-icon-sm"
                  >
                    <MinusIcon />
                  </button>
                  <button
                    onMouseDown={() =>
                      onEventCurrentAgeField("start-count-increment")
                    }
                    onMouseUp={() => onEventCurrentAgeField("stop-count")}
                    onMouseLeave={() => onEventCurrentAgeField("stop-count")}
                    onClick={() => onEventCurrentAgeField("increment")}
                    className="btn-tertiary-icon-sm"
                  >
                    <PlusIcon />
                  </button>
                </div>
              </div>
            </div>
            <div className="flex flex-1 flex-col gap-y-2">
              <span className="text-pn-callout-emphasis text-pn_gray-950 bg-[#F4E34E] w-fit">
                I would like to retire at:
              </span>
              <div className="flex items-center gap-x-2">
                <div className="flex items-center gap-x-1 flex-1 md:flex-[none] md:gap-x-3">
                  <span className="text-pn-large-title text-pn_gray-950">
                    {retirementAgeField}
                  </span>
                  <sub className="text-pn-caption-regular pt-3 text-pn_gray-600">
                    years old
                  </sub>
                </div>
                <div className="flex items-center gap-x-4">
                  <button
                    onMouseDown={() =>
                      onEventRetirementAgeField("start-count-decrement")
                    }
                    onMouseUp={() => onEventRetirementAgeField("stop-count")}
                    onMouseLeave={() => onEventRetirementAgeField("stop-count")}
                    onClick={() => onEventRetirementAgeField("decrement")}
                    className="btn-tertiary-icon-sm"
                  >
                    <MinusIcon />
                  </button>
                  <button
                    onMouseDown={() =>
                      onEventRetirementAgeField("start-count-increment")
                    }
                    onMouseUp={() => onEventRetirementAgeField("stop-count")}
                    onMouseLeave={() => onEventRetirementAgeField("stop-count")}
                    onClick={() => onEventRetirementAgeField("increment")}
                    className="btn-tertiary-icon-sm"
                  >
                    <PlusIcon />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="divider-dotted-gray my-2 hidden md:block"></div>

          <div>
            <PennyInput
              value={currentPensionFundField}
              onChange={onInputChecker}
              label="My current pension fund is worth:"
              tags="Optional"
              placeholder="0.00"
              typography="large_title"
              messageicon={<InfoIcon />}
              message={`You don't need to have a pension fund to be able to open a Penny account`}
              icon={<span className="text-pn-large-title">€</span>}
              labelHighlighted
            />
          </div>
          <CardInformation
            variant="safe"
            description={
              <div className="text-callout-regular">
                Your information is always safe with Penny.{" "}
                <Link to="#" className="text-pn-callout-link text-pn_cyan-500">
                  Learn more about how we do it.
                </Link>
              </div>
            }
          />
        </div>
      </div>

      <div className="mt-10">
        <BottomBar
          onNext={() => onSubmitNext()}
          onBack={() => navigate(-1)}
          labelNext="Calculate"
          layout="main_back"
        />
      </div>
    </>
  );
};
export default PensionStepOne;
